import React from 'react';
import { Sparkles } from 'lucide-react';
import { PREMIUM_PRICING } from '@/config/pricingConfig';

/**
 * LaunchOfferBadge
 * Affiche l'offre de lancement Premium de manière élégante
 * 
 * Props:
 * - isEnglish: boolean - Afficher en anglais
 * - variant: 'default' | 'compact' | 'banner' - Style d'affichage
 * - className: string - Classes CSS additionnelles
 */
const LaunchOfferBadge = ({ isEnglish = false, variant = 'default', className = '' }) => {
  const offer = PREMIUM_PRICING.launchOffer;

  if (variant === 'compact') {
    return (
      <div className={`inline-flex items-center gap-2 bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-full px-4 py-2 ${className}`}>
        <Sparkles className="w-4 h-4 text-purple-400" />
        <span className="text-purple-300 text-sm font-semibold">
          {isEnglish ? offer.badgeEn : offer.badge}
        </span>
        <span className="text-white text-sm font-bold">
          {isEnglish ? offer.priceEn : offer.price}
        </span>
        <span className="text-white/60 text-xs">
          {isEnglish ? '1st year' : '1ère année'}
        </span>
      </div>
    );
  }

  if (variant === 'banner') {
    return (
      <div 
        className={`relative bg-gradient-to-r from-purple-600/10 via-pink-600/10 to-purple-600/10 border border-purple-500/30 rounded-2xl p-6 backdrop-blur-sm ${className}`}
        style={{
          boxShadow: '0 0 30px rgba(147, 51, 234, 0.15)'
        }}
      >
        {/* Glow effect */}
        <div 
          className="absolute inset-0 rounded-2xl opacity-30"
          style={{
            background: 'radial-gradient(circle at 50% 50%, rgba(147, 51, 234, 0.3) 0%, transparent 70%)',
            filter: 'blur(20px)'
          }}
        />
        
        <div className="relative z-10">
          {/* Title with icon */}
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-purple-400" />
            <h3 className="text-lg font-bold text-white">
              {isEnglish ? offer.titleEn : offer.title}
            </h3>
          </div>
          
          {/* Subtitle */}
          <p className="text-purple-300 text-sm font-medium mb-4">
            {isEnglish ? offer.subtitleEn : offer.subtitle}
          </p>
          
          {/* Price display - support FR and EN formats */}
          <div className="flex items-baseline gap-2 mb-2">
            {isEnglish ? (
              <div className="flex items-baseline">
                <span className="text-4xl font-bold text-white">
                  {offer.priceEn.split('.')[0]}
                </span>
                <span className="text-2xl font-bold text-white">
                  .{offer.priceEn.split('.')[1]}
                </span>
              </div>
            ) : (
              <div className="flex items-baseline">
                <span className="text-4xl font-bold text-white">
                  {offer.price.split(',')[0]}
                </span>
                <span className="text-2xl font-bold text-white">
                  ,{offer.price.split(',')[1]}
                </span>
              </div>
            )}
            <span className="text-white/70 text-base">
              {isEnglish ? 'for the first year' : 'la première année'}
            </span>
          </div>
          
          {/* Monthly equivalent badge */}
          <div className="inline-flex items-center gap-1 bg-purple-500/20 border border-purple-500/30 rounded-full px-3 py-1.5 mb-3">
            <span className="text-purple-300 text-sm font-semibold">
              {isEnglish ? offer.monthlyEquivalentEn : offer.monthlyEquivalent}{isEnglish ? offer.monthlyEquivalentPeriodEn : offer.monthlyEquivalentPeriod}
            </span>
          </div>
          
          {/* Regular price strikethrough */}
          <div className="flex items-center gap-2 mb-4">
            <span className="text-white/40 text-sm line-through">
              {isEnglish ? `then ${offer.regularPriceEn}` : `puis ${offer.regularPrice}`}
            </span>
            <span className="text-white/60 text-sm">
              {isEnglish ? offer.periodEn : offer.period}
            </span>
          </div>
          
          {/* Description */}
          <p className="text-white/70 text-sm italic">
            {isEnglish ? offer.descriptionEn : offer.description}
          </p>
        </div>
      </div>
    );
  }

  // Default variant
  return (
    <div 
      className={`bg-gradient-to-br from-purple-600/15 to-pink-600/15 border border-purple-500/30 rounded-xl p-4 ${className}`}
      style={{
        boxShadow: '0 0 20px rgba(147, 51, 234, 0.1)'
      }}
    >
      {/* Badge */}
      <div className="inline-flex items-center gap-2 bg-purple-600/20 rounded-full px-3 py-1 mb-3">
        <Sparkles className="w-3.5 h-3.5 text-purple-400" />
        <span className="text-purple-300 text-xs font-semibold uppercase tracking-wide">
          {isEnglish ? offer.badgeEn : offer.badge}
        </span>
      </div>
      
      {/* Title */}
      <h4 className="text-white font-semibold text-base mb-2">
        {isEnglish ? offer.titleEn.replace('🎉 ', '') : offer.title.replace('🎉 ', '')}
      </h4>
      
      {/* Subtitle */}
      <p className="text-purple-300 text-sm mb-3">
        {isEnglish ? offer.subtitleEn : offer.subtitle}
      </p>
      
      {/* Price */}
      <div className="flex items-baseline gap-2 mb-2">
        <span className="text-2xl font-bold text-white">
          {isEnglish ? offer.priceEn : offer.price}
        </span>
        <span className="text-white/70 text-sm">
          {isEnglish ? 'for the first year' : 'la première année'}
        </span>
      </div>
      
      {/* Monthly equivalent badge */}
      <div className="inline-flex items-center gap-1 bg-purple-500/20 border border-purple-500/30 rounded-full px-2 py-1 mb-2">
        <span className="text-purple-300 text-xs font-semibold">
          {isEnglish ? offer.monthlyEquivalentEn : offer.monthlyEquivalent}{isEnglish ? offer.monthlyEquivalentPeriodEn : offer.monthlyEquivalentPeriod}
        </span>
      </div>
      
      {/* Regular price */}
      <p className="text-white/50 text-xs mb-3">
        {isEnglish 
          ? `then ${offer.regularPriceEn}${offer.periodEn}` 
          : `puis ${offer.regularPrice}${offer.period}`}
      </p>
      
      {/* Description */}
      <p className="text-white/60 text-xs italic leading-relaxed">
        {isEnglish ? offer.descriptionEn : offer.description}
      </p>
    </div>
  );
};

export default LaunchOfferBadge;
